/**
 * `<ContactDetailRoute>` — `/w/:slug/contacts/:contactId`.
 *
 * Per FD § 2.2. Header (avatar + name + actions) + Profile / Sessions /
 * Runs / Memories tabs. Sessions, Runs, and Memories are lazy-loaded on
 * tab activation per NFR-PERF-10.
 *
 * 404 from the API surfaces as a full-page not-found view per FD § 4.
 * Soft-deleted contacts: see FD § 2.5 — deleted detail view is only
 * reachable via the `?status=deleted` list flow; direct navigation
 * surfaces 404 (P3 indistinguishability).
 */

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  cn,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  ChevronRight,
  Copy,
  Cpu,
  Headset,
  MessagesSquare,
  Power,
  Trash2,
  User,
  type LucideIcon,
} from "../components/icons";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";

import { useAbility } from "@cometchat-ai/auth/client";
import { ContactMemoriesTab } from "../components/contacts/ContactMemoriesTab";
import { ContactSteersSection } from "../components/contacts/ContactSteersSection";
import { ContactRunsTab } from "../components/contacts/ContactRunsTab";
import { ContactSessionsTab } from "../components/contacts/ContactSessionsTab";
import { ContactStatusBadge, deriveStatus } from "../components/contacts/ContactStatusBadge";
import { DeleteContactDialog } from "../components/contacts/DeleteContactDialog";
import { DisableContactDialog } from "../components/contacts/DisableContactDialog";
import { ApiError } from "../lib/api/errors";
import { PageShell } from "../components/page-shell";
import { usePageBreadcrumb } from "../layouts/breadcrumb-trail";
import { useOptimisticFlipMutation } from "../lib/api/optimistic";
import { useContactsClient } from "../lib/api/use-contacts-client";
import type { Contact, ContactProfilePatch, Paginated, SessionSummary } from "../lib/api/types";
import { FINAL_FONT } from "../layouts/final-polish";

type ContactSection = "profile" | "memories" | "instructions" | "runs" | "sessions" | "danger";

const CONTACT_SECTIONS: { id: ContactSection; labelKey: string; icon: LucideIcon }[] = [
  { id: "profile", labelKey: "detail.tabs.profile", icon: User },
  { id: "memories", labelKey: "detail.tabs.memories", icon: Cpu },
  { id: "instructions", labelKey: "detail.tabs.instructions", icon: Headset },
  { id: "runs", labelKey: "detail.tabs.runs", icon: Activity },
  { id: "sessions", labelKey: "detail.tabs.sessions", icon: MessagesSquare },
  { id: "danger", labelKey: "detail.tabs.danger", icon: AlertTriangle },
];

export function ContactDetailRoute() {
  const { contactId } = useParams<{ contactId: string }>();
  const { t } = useTranslation("contacts");
  const navigate = useNavigate();
  const client = useContactsClient();
  const qc = useQueryClient();
  // Standing-instructions tab is operator-only (same gate as steer writes).
  const canSteer = useAbility().can("monitor", "Conversation");
  const baseSections = CONTACT_SECTIONS.filter((s) => s.id !== "instructions" || canSteer);

  const [disableOpen, setDisableOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [section, setSection] = useState<ContactSection>("profile");
  useEffect(() => {
    setSection("profile");
  }, [contactId]);

  const detailKey = ["contacts", "detail", contactId ?? ""] as const;
  const listKey = ["contacts", "list"] as const;

  const query = useQuery({
    queryKey: detailKey,
    queryFn: () => client.get(contactId!),
    enabled: Boolean(contactId),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const disableMutation = useOptimisticFlipMutation<
    Contact,
    { contactId: string; reason?: string }
  >({
    queryClient: qc,
    listKey,
    detailKey: () => detailKey,
    toId: (v) => v.contactId,
    mutationFn: (v) => client.disable(v.contactId, v.reason),
    optimisticPatch: (c) => ({ ...c, isDisabled: true }),
    successToast: t("disable.toast"),
    errorToast: t("disable.error"),
  });

  const enableMutation = useOptimisticFlipMutation<Contact, string>({
    queryClient: qc,
    listKey,
    detailKey: () => detailKey,
    mutationFn: (id) => client.enable(id),
    optimisticPatch: (c) => ({ ...c, isDisabled: false }),
    successToast: t("enable.toast"),
    errorToast: t("enable.error"),
  });

  const deleteMutation = useMutation({
    mutationFn: () => client.softDelete(contactId!),
    onSuccess: () => {
      toast.success(t("delete.toast"));
      void qc.invalidateQueries({ queryKey: listKey });
      navigate("../contacts?status=deleted");
    },
    onError: () => toast.error(t("delete.error")),
  });

  usePageBreadcrumb(query.data ? [{ label: query.data.displayName ?? query.data.userId }] : []);

  if (!contactId) {
    return <NotFoundView />;
  }

  if (query.isPending) {
    return (
      <div className="space-y-4" data-testid="contact-detail-loading">
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-8 w-1/3" />
      </div>
    );
  }

  if (query.error) {
    const err = query.error;
    if (err instanceof ApiError) {
      if (err.isNotFound()) return <NotFoundView />;
      if (err.isForbidden()) return <ForbiddenView />;
    }
    return (
      <Alert variant="destructive" data-testid="contact-detail-error">
        <AlertDescription>{err instanceof Error ? err.message : String(err)}</AlertDescription>
      </Alert>
    );
  }

  const contact = query.data;
  const visibleName = contact.displayName ?? contact.userId;
  const status = deriveStatus(contact);
  const isDeleted = status === "deleted";
  // A deleted contact can't be disabled or deleted again, so hide the Danger
  // zone for it (mirrors the old actions menu, which was `!isDeleted` only).
  const sections = isDeleted ? baseSections.filter((s) => s.id !== "danger") : baseSections;
  const activeMeta = sections.find((s) => s.id === section) ?? sections[0]!;
  const ActiveSectionIcon = activeMeta.icon;

  // Warm each tab's query on hover/focus so clicking it shows cached data
  // instead of a loading flash. Mirrors the row-hover prefetch on the list and
  // keeps the tabs lazy (NFR-PERF-10) — data is fetched on intent, not upfront.
  const prefetchSection = (id: ContactSection) => {
    if (id === "memories") {
      void qc.prefetchQuery({
        queryKey: ["contacts", "memories", contact.id],
        queryFn: () => client.listMemories(contact.id),
        staleTime: 30_000,
      });
    } else if (id === "runs") {
      void qc.prefetchQuery({
        queryKey: ["contacts", "runs", contact.id, undefined],
        queryFn: () => client.runs(contact.id, { cursor: undefined, limit: 10 }),
        staleTime: 30_000,
      });
    } else if (id === "sessions") {
      void qc.prefetchInfiniteQuery({
        queryKey: ["contacts", "sessions", contact.id],
        queryFn: ({ pageParam }) => client.sessions(contact.id, { cursor: pageParam, limit: 25 }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (last: Paginated<SessionSummary>) => last.nextCursor ?? undefined,
        staleTime: 30_000,
      });
    }
  };

  return (
    <PageShell
      data-testid="contact-detail-route"
      className="p-0"
      title={
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("../contacts")}
            className="text-base font-semibold tracking-[0.01em] text-foreground transition-colors hover:text-muted-foreground"
          >
            {t("nav.contacts")}
          </button>
          <ChevronRight
            className="h-3.5 w-3.5 shrink-0 text-[color:var(--color-fg-subtle)]"
            aria-hidden="true"
          />
          <span className="truncate text-base font-medium tracking-[0.01em] text-muted-foreground">
            {visibleName}
          </span>
          <span className="h-4 w-px shrink-0 bg-border" aria-hidden="true" />
          <ContactStatusBadge contact={contact} />
          {contact.identityVerified ? (
            <span
              className="inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-xs font-medium"
              style={{
                color: "var(--color-accent)",
                background: "color-mix(in oklch, var(--color-accent) 12%, transparent)",
              }}
            >
              {t("list.table.verified")}
            </span>
          ) : null}
        </div>
      }
    >
      <div className="flex h-full min-h-0 flex-col lg:h-[calc(100svh-3.5rem)]">
        {isDeleted ? (
          <div className="px-6 pt-4">
            <Alert data-testid="contact-deleted-banner">
              <AlertTitle>
                {t("detail.deletedBanner", {
                  date: contact.deletedAt ? contact.deletedAt.toLocaleDateString() : "—",
                })}
              </AlertTitle>
              <AlertDescription>{t("detail.restoreDisabled")}</AlertDescription>
            </Alert>
          </div>
        ) : null}

        <div className="flex min-w-0 flex-1 flex-col overflow-hidden px-6 py-6">
          <div className="mx-auto flex min-h-0 w-full max-w-6xl flex-1 overflow-hidden rounded-xl border border-border bg-card">
            {/* left — section nav */}
            <nav className="flex w-56 shrink-0 flex-col gap-0.5 overflow-auto border-r border-border bg-muted/30 p-2">
              {sections.map(({ id, labelKey, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setSection(id)}
                  onMouseEnter={() => prefetchSection(id)}
                  onFocus={() => prefetchSection(id)}
                  data-active={section === id}
                  data-testid={`contact-detail-tab-${id}`}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-2sm tracking-wide-sm transition-colors",
                    section === id
                      ? "bg-[color:color-mix(in_oklch,var(--color-fg)_6%,transparent)] font-medium text-foreground"
                      : "text-[color:var(--color-fg-muted)] hover:bg-[color:color-mix(in_oklch,var(--color-fg)_3%,transparent)] hover:text-foreground",
                  )}
                >
                  <Icon
                    className={cn(
                      "h-4 w-4 shrink-0",
                      section === id
                        ? "text-[color:color-mix(in_oklch,var(--color-fg)_55%,transparent)]"
                        : "text-[color:var(--color-fg-muted)]",
                    )}
                    aria-hidden="true"
                    variant={section === id ? "Bold" : undefined}
                  />
                  <span className="truncate">{t(labelKey as never)}</span>
                </button>
              ))}
            </nav>

            {/* right — section content */}
            <div className="min-w-0 flex-1 overflow-auto p-5">
              <div className="mb-5 flex items-center gap-2.5">
                <ActiveSectionIcon
                  className="h-4 w-4 shrink-0 text-foreground"
                  aria-hidden="true"
                  variant="Outline"
                />
                <h2 className="text-sm font-semibold tracking-wide-sm text-foreground">
                  {t(activeMeta.labelKey as never)}
                </h2>
              </div>

              {section === "profile" ? (
                <ProfileSection contact={contact} isDeleted={isDeleted} />
              ) : null}
              {section === "memories" ? <ContactMemoriesTab contactId={contact.id} /> : null}
              {section === "instructions" ? <ContactSteersSection contactId={contact.id} /> : null}
              {section === "runs" ? <ContactRunsTab contactId={contact.id} /> : null}
              {section === "sessions" ? <ContactSessionsTab contactId={contact.id} /> : null}
              {section === "danger" ? (
                <DangerZoneSection
                  contact={contact}
                  busy={disableMutation.isPending || enableMutation.isPending}
                  onEnable={() => enableMutation.mutate(contact.id)}
                  onDisable={() => setDisableOpen(true)}
                  onDelete={() => setDeleteOpen(true)}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <DisableContactDialog
        open={disableOpen}
        onOpenChange={setDisableOpen}
        contactName={visibleName}
        busy={disableMutation.isPending}
        onConfirm={(reason) => {
          disableMutation.mutate(
            { contactId: contact.id, reason },
            { onSettled: () => setDisableOpen(false) },
          );
        }}
      />
      <DeleteContactDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        contactUserId={contact.userId}
        contactDisplayName={contact.displayName}
        busy={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
      />
    </PageShell>
  );
}

/** A labelled field — label above an always-editable input, matching the Final
 *  ContactDetailView. `trailing` renders an affordance inside the input (copy). */
function ProfileField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-2sm font-medium text-foreground">{label}</Label>
      {children}
    </div>
  );
}

/**
 * Destructive actions, surfaced as a dedicated nav section. Reuses the existing
 * disable/enable/delete flows (the same dialogs and mutations the toolbar menu
 * used before); this is just a clearer home for them.
 */
function DangerZoneSection({
  contact,
  busy,
  onEnable,
  onDisable,
  onDelete,
}: {
  contact: Contact;
  busy: boolean;
  onEnable: () => void;
  onDisable: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation("contacts");
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0 space-y-0.5">
          <p className="text-2sm font-medium text-foreground">
            {contact.isDisabled ? t("detail.danger.enableTitle") : t("detail.danger.disableTitle")}
          </p>
          <p className="text-2sm text-muted-foreground">
            {contact.isDisabled ? t("detail.danger.enableBody") : t("detail.danger.disableBody")}
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={contact.isDisabled ? onEnable : onDisable}
          className="shrink-0 gap-1.5 rounded-md hover:bg-secondary-hover"
          data-testid="contact-danger-disable"
        >
          <Power className="h-4 w-4" aria-hidden="true" />
          {contact.isDisabled ? t("actions.enable") : t("actions.disable")}
        </Button>
      </div>
      <div className="-mx-6 border-t border-dashed border-border" />
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0 space-y-0.5">
          <p className="text-2sm font-medium text-foreground">{t("detail.danger.deleteTitle")}</p>
          <p className="text-2sm text-muted-foreground">{t("detail.danger.deleteBody")}</p>
        </div>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          onClick={onDelete}
          className="shrink-0 gap-1.5 rounded-md"
          data-testid="contact-danger-delete"
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
          {t("actions.delete")}
        </Button>
      </div>
    </div>
  );
}

/**
 * Editable profile — an always-editable field grid + a read-only metadata blob,
 * mirroring the Final ContactDetailView. "Save changes" persists the editable
 * profile fields via the update API; Status maps to enable/disable. User ID is
 * immutable (set at creation), so it's read-only with a copy affordance.
 */
function ProfileSection({ contact, isDeleted }: { contact: Contact; isDeleted: boolean }) {
  const { t } = useTranslation("contacts");
  const client = useContactsClient();
  const qc = useQueryClient();
  const detailKey = ["contacts", "detail", contact.id] as const;

  const toFormStatus = (c: Contact): "active" | "disabled" =>
    c.isDisabled ? "disabled" : "active";

  const [form, setForm] = useState({
    email: contact.email ?? "",
    phone: contact.phone ?? "",
    locale: contact.locale ?? "",
    timezone: contact.timezone ?? "",
    status: toFormStatus(contact),
  });

  // Re-seed when the underlying contact changes (navigation or a refetch).
  useEffect(() => {
    setForm({
      email: contact.email ?? "",
      phone: contact.phone ?? "",
      locale: contact.locale ?? "",
      timezone: contact.timezone ?? "",
      status: toFormStatus(contact),
    });
  }, [
    contact.id,
    contact.email,
    contact.phone,
    contact.locale,
    contact.timezone,
    contact.isDisabled,
  ]);

  const setField = (key: keyof typeof form, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const saveMutation = useMutation({
    mutationFn: async () => {
      const trimmed = (v: string) => (v.trim() === "" ? null : v.trim());
      const patch: ContactProfilePatch = {
        email: trimmed(form.email),
        phone: trimmed(form.phone),
        locale: trimmed(form.locale),
        timezone: trimmed(form.timezone),
      };
      await client.update(contact.id, patch);
      const wantDisabled = form.status === "disabled";
      if (wantDisabled !== contact.isDisabled) {
        await (wantDisabled ? client.disable(contact.id) : client.enable(contact.id));
      }
    },
    onSuccess: () => {
      toast.success(t("edit.toast"));
      void qc.invalidateQueries({ queryKey: detailKey });
      void qc.invalidateQueries({ queryKey: ["contacts", "list"] });
    },
    onError: () => toast.error(t("edit.error")),
  });

  const dirty =
    form.email !== (contact.email ?? "") ||
    form.phone !== (contact.phone ?? "") ||
    form.locale !== (contact.locale ?? "") ||
    form.timezone !== (contact.timezone ?? "") ||
    form.status !== toFormStatus(contact);

  const inputCls = "h-9 rounded-lg text-2sm";
  const disabled = isDeleted || saveMutation.isPending;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-y-4">
        <ProfileField label={t("form.userId")}>
          <div className="relative">
            <Input
              readOnly
              value={contact.userId}
              className={cn(inputCls, "pr-9")}
              data-testid="contact-detail-user-id"
            />
            <button
              type="button"
              onClick={() => void navigator.clipboard?.writeText(contact.userId)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
              aria-label={t("form.userId")}
              data-testid="contact-detail-copy-external-id"
            >
              <Copy className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        </ProfileField>

        <ProfileField label={t("form.email")}>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setField("email", e.target.value)}
            placeholder="ada@example.com"
            disabled={disabled}
            className={inputCls}
            data-testid="contact-detail-email"
          />
        </ProfileField>

        <ProfileField label={t("form.phone")}>
          <Input
            value={form.phone}
            onChange={(e) => setField("phone", e.target.value)}
            placeholder="+1 415 555 0101"
            disabled={disabled}
            className={inputCls}
            data-testid="contact-detail-phone"
          />
        </ProfileField>

        <ProfileField label={t("form.locale")}>
          <Input
            value={form.locale}
            onChange={(e) => setField("locale", e.target.value)}
            placeholder="en-US"
            disabled={disabled}
            className={inputCls}
            data-testid="contact-detail-locale"
          />
        </ProfileField>

        <ProfileField label={t("form.timezone")}>
          <Input
            value={form.timezone}
            onChange={(e) => setField("timezone", e.target.value)}
            placeholder="America/Los_Angeles"
            disabled={disabled}
            className={inputCls}
            data-testid="contact-detail-timezone"
          />
        </ProfileField>

        <ProfileField label={t("form.status")}>
          <Select
            value={form.status}
            onValueChange={(v) => setField("status", v)}
            disabled={disabled}
          >
            <SelectTrigger className={inputCls} data-testid="contact-detail-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">{t("status.active")}</SelectItem>
              <SelectItem value="disabled">{t("status.disabled")}</SelectItem>
            </SelectContent>
          </Select>
        </ProfileField>
      </div>

      <div className="-mx-6 border-t border-dashed border-border" />

      <div className="space-y-1.5">
        <span className="block text-2sm font-medium text-foreground">{t("detail.metadata")}</span>
        <pre
          style={{ fontFamily: FINAL_FONT }}
          className="overflow-auto rounded-lg bg-muted p-4 text-2sm leading-relaxed text-foreground"
          data-testid="contact-detail-metadata"
        >
          {JSON.stringify(contact.metadata, null, 2)}
        </pre>
      </div>

      {!isDeleted ? (
        <>
          <div className="-mx-6 border-t border-dashed border-border" />
          <div className="flex justify-end">
            <Button
              type="button"
              size="sm"
              className="h-9 rounded-lg px-4"
              onClick={() => saveMutation.mutate()}
              disabled={disabled || !dirty}
              data-testid="contact-detail-save"
            >
              {t("form.save")}
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}

function NotFoundView() {
  const { t } = useTranslation("contacts");
  return (
    <div className="mx-auto max-w-md py-16 text-center" data-testid="contact-detail-not-found">
      <h2 className="text-xl font-semibold">{t("detail.notFoundTitle")}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{t("detail.notFoundBody")}</p>
      <Button asChild className="mt-4" variant="outline">
        <Link to="../contacts">{t("detail.back")}</Link>
      </Button>
    </div>
  );
}

function ForbiddenView() {
  const { t } = useTranslation("contacts");
  return (
    <div className="mx-auto max-w-md py-16 text-center" data-testid="contact-detail-forbidden">
      <h2 className="text-xl font-semibold">{t("detail.forbiddenTitle")}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{t("detail.forbiddenBody")}</p>
      <Button asChild className="mt-4" variant="outline">
        <Link to="../contacts">{t("detail.back")}</Link>
      </Button>
    </div>
  );
}
